import type { HandView, SeatView } from '@tctp/protocol';
import { useEffect, useRef } from 'react';
import { allInSound, checkSound, chipSound, discardSound, winSound } from './notify';
import { soundEnabled } from './sound';

/**
 * Plays a sound for every action appended to the hand since the last render (discard → flick, check → knock,
 * call / bet / raise → chips, or the all-in push when it leaves the seat all in; a fold, and your own locked discard, → the card shove) and, once the pot is taken and the reveal has landed (`shownShowdown`), the win sound —
 * only on the winner's own screen (`me` took a share of a pot); losers and spectators just see the animation.
 * Nothing plays for what was already in the snapshot when a hand was first seen.
 */
export function useActionSounds(hand: HandView | null, shownShowdown: boolean, me: SeatView | null = null, seats: ReadonlyArray<SeatView | null> = []): void {
  const seen = useRef<{ handId: string | null; count: number; won: boolean }>({ handId: null, count: 0, won: false });
  useEffect(() => {
    const s = seen.current;
    if (!hand) return;
    const won = hand.results !== null && shownShowdown;
    if (hand.handId !== s.handId) {
      seen.current = { handId: hand.handId, count: hand.actions.length, won };
      return;
    }
    if (soundEnabled()) {
      for (const a of hand.actions.slice(s.count)) {
        if (a.type === 'check') checkSound();
        else if (a.type === 'call' || a.type === 'raise') { if (seats[a.seat]?.allIn) allInSound(); else chipSound(); }   // the push that left them all in has its own sound
        else if (a.type === 'fold' || (a.type === 'discardLocked' && a.seat === me?.seat)) discardSound();   // a card leaving the hand sounds the same either way; other players' discards are silent
      }
      if (won && !s.won && me !== null && hand.results!.awards.some(a => a.shares.some(sh => sh.seat === me.seat))) winSound();
    }
    s.count = hand.actions.length;
    s.won = s.won || won;
  }, [hand, shownShowdown, me, seats]);
}
